import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@service/auth/auth.service';
import { UserService } from '@service/auth/user.service';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import { RETURN_URL_PARAM, rememberReturnUrl } from '../utils/post-login-redirect.util';
import {
  isDeactivatedUserError,
  isProfileNotFoundError,
  isTransientProfileError,
} from '../utils/user-profile-error.util';

/**
 * Active user guard to block deactivated users from accessing any routes
 *
 * This guard ensures that deactivated users (status === 'inactive') cannot access
 * any application features, even if they have admin permissions. Deactivated users
 * are redirected to the /profile/disable page.
 *
 * CRITICAL SECURITY: This check happens before any role-based permission checks,
 * ensuring that deactivation takes precedence over all other permissions.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'maintenance',
 *   canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
 *   data: { requiredRoles: [UserRole.ADMIN] },
 *   loadChildren: () => import('./maintenance.module').then(m => m.MaintenanceModule)
 * }
 * ```
 */

/**
 * Storage key for caching user active status
 * This reduces API calls while still checking status regularly
 */
const USER_STATUS_CACHE_KEY = 'user_active_status';
const USER_STATUS_CACHE_DURATION = 60000; // 1 minute cache

interface CachedUserStatus {
  status: string;
  timestamp: number;
}

/**
 * Get cached user status if still valid
 */
function getCachedUserStatus(): string | null {
  try {
    const cached = sessionStorage.getItem(USER_STATUS_CACHE_KEY);
    if (cached) {
      const parsedCache: CachedUserStatus = JSON.parse(cached);
      if (Date.now() - parsedCache.timestamp < USER_STATUS_CACHE_DURATION) {
        return parsedCache.status;
      }
    }
  } catch (e) {
    // sessionStorage might not be available or JSON parse failed
  }
  return null;
}

/**
 * Cache user status for subsequent checks
 */
function cacheUserStatus(status: string): void {
  try {
    const cacheData: CachedUserStatus = {
      status,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(USER_STATUS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    // sessionStorage might not be available
  }
}

/**
 * Clear cached user status (e.g., on logout or when status changes)
 */
export function clearUserStatusCache(): void {
  try {
    sessionStorage.removeItem(USER_STATUS_CACHE_KEY);
  } catch (e) {
    // sessionStorage might not be available
  }
}

/**
 * Recover from a user profile that could not be read.
 *
 * Internal users are created on the fly at first login, so a missing profile is
 * an expected state for them - not a deactivation. POST /users/ensure is
 * idempotent, so calling it here either creates the profile or returns the
 * existing one, and the user continues to the page they asked for (home).
 *
 * External users are provisioned by an admin (their "#EXT#" mailnickname is
 * resolved through a USER_ALIAS record), so for them a missing profile stays an
 * error and they keep landing on the disable page as before.
 */
function recoverMissingProfile(
  authService: AuthService,
  userService: UserService,
  router: Router,
  url: string,
): Observable<boolean> {
  return from(authService.isInternalUser()).pipe(
    switchMap((isInternal) => {
      if (!isInternal) {
        console.warn('No profile for external user - redirecting to disable page');
        cacheUserStatus('inactive');
        router.navigate(['/profile/disable']);
        return of(false);
      }

      return userService.ensureUserAccount().pipe(
        map((result) => {
          const status = result?.user?.status || 'active';

          if (status === 'inactive') {
            console.warn('Internal user account is deactivated, redirecting to disable page');
            cacheUserStatus('inactive');
            router.navigate(['/profile/disable']);
            return false;
          }

          console.log(`Internal user account ensured, access granted to ${url}`);
          cacheUserStatus('active');
          // The profile was just written - drop any stale cached copy
          userService.invalidateCurrentUserCache();
          return true;
        }),
        catchError((error) => {
          if (isDeactivatedUserError(error)) {
            console.warn('Internal user is deactivated, redirecting to disable page');
            cacheUserStatus('inactive');
            router.navigate(['/profile/disable']);
            return of(false);
          }

          // Could not confirm either way. Let the authenticated user through
          // without caching a status - the backend still rejects the account on
          // every API call if it really is deactivated, and the next navigation
          // re-checks. Sending them to the disable page here would be wrong.
          console.warn('Could not ensure user account, allowing access without cache:', error);
          return of(true);
        }),
      );
    }),
  );
}

/**
 * Guard to check if user account is active
 * Blocks deactivated users from accessing any protected routes
 *
 * This check takes precedence over all other permission checks:
 * - Even ADMIN users are blocked if deactivated
 * - The only accessible route for deactivated users is /profile/disable
 */
export const activeUserGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  // Allow access to the disable page (otherwise deactivated users couldn't see it)
  if (state.url.includes('/profile/disable')) {
    return true;
  }

  // CRITICAL: Check if user is authenticated first
  // If not authenticated, redirect to login (not disable page)
  // This prevents the guard from making API calls that will fail
  return from(authService.isAuthenticated()).pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        console.log('User not authenticated, redirecting to login');
        // Keep the requested page so signing in returns to it instead of /profile
        const returnUrl = rememberReturnUrl(state.url);
        router.navigate(['/login'], {
          queryParams: returnUrl ? { [RETURN_URL_PARAM]: returnUrl } : {},
        });
        return of(false);
      }

      // Check cached status first for performance
      const cachedStatus = getCachedUserStatus();
      if (cachedStatus === 'inactive') {
        console.warn('Cached status indicates user is deactivated, redirecting to disable page');
        router.navigate(['/profile/disable']);
        return of(false);
      }

      // If cached status is active, allow and verify in background (non-blocking)
      if (cachedStatus === 'active') {
        // Verify in background but allow access immediately
        // This provides a good balance between security and UX
        verifyUserStatusInBackground(userService, router);
        return of(true);
      }

      // No cache - need to fetch status from API
      return userService.getCurrentUser().pipe(
        map((userProfile) => {
          const status = userProfile.status || 'active';
          cacheUserStatus(status);

          if (status === 'inactive') {
            console.warn(
              `User ${userProfile.email} is deactivated, blocking access to ${state.url}`,
            );
            router.navigate(['/profile/disable']);
            return false;
          }

          console.log(`User ${userProfile.email} is active, access granted to ${state.url}`);
          return true;
        }),
        catchError((error) => {
          console.warn('Error checking user status:', error);

          // Check if it's an authentication error (no token)
          const errorMessage = error?.message || error?.error?.message || '';
          if (
            errorMessage.includes('Authentication required') ||
            errorMessage.includes('No ID token')
          ) {
            console.warn('Authentication error, redirecting to login');
            router.navigate(['/login']);
            return of(false);
          }

          // Check for deactivation errors using comprehensive error structure check
          if (isDeactivatedUserError(error)) {
            console.warn('User is deactivated (from API error), redirecting to disable page');
            cacheUserStatus('inactive');
            router.navigate(['/profile/disable']);
            return of(false);
          }

          // No profile yet, or the profile call did not get through: this says
          // nothing about the account being deactivated. On a first login the
          // account is still being created, so try to complete that instead.
          if (isProfileNotFoundError(error) || isTransientProfileError(error)) {
            return recoverMissingProfile(authService, userService, router, state.url);
          }

          // For any other API errors, be conservative and redirect to disable page
          // This prevents deactivated users from accessing the app if error structure changes
          console.warn('API error occurred - redirecting to disable page as safety measure');
          cacheUserStatus('inactive');
          router.navigate(['/profile/disable']);
          return of(false);
        }),
      );
    }),
  );
};

/**
 * Verify user status in background (non-blocking)
 * Used when we have cached active status to verify it's still valid
 */
function verifyUserStatusInBackground(userService: UserService, router: Router): void {
  // Use getCurrentUserFresh() to bypass the session cache and always hit the server
  userService.getCurrentUserFresh().subscribe({
    next: (userProfile) => {
      const status = userProfile.status || 'active';
      cacheUserStatus(status);

      if (status === 'inactive') {
        console.warn('Background check: User is now deactivated, redirecting to disable page');
        userService.invalidateCurrentUserCache();
        router.navigate(['/profile/disable']);
      }
    },
    error: (error) => {
      // A profile that is not readable yet (first login) is not a deactivation
      if (isProfileNotFoundError(error)) {
        return;
      }

      // Use comprehensive error check for deactivation
      if (isDeactivatedUserError(error)) {
        console.warn('Background check: User is deactivated, redirecting to disable page');
        cacheUserStatus('inactive');
        userService.invalidateCurrentUserCache();
        router.navigate(['/profile/disable']);
      }
    },
  });
}
